import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { crmApi } from "../../lib/crm-api";
import { Chip } from "../sales/NewCallDialog";
import { cn } from "../ui/cn";

/**
 * Who to invite, from outside NetXL.
 *
 * The account's contacts are offered as one tap each, because that is who it
 * usually is -- and underneath them is an ordinary text box that takes any
 * address at all. Both halves matter. A picker alone cannot invite their IT
 * provider, the installer, or the colleague who was cc'd on the thread, and a
 * meeting booked without those people is a meeting the wrong half of the room
 * turns up to. A text box alone means typing an address somebody already has
 * on file, which is how it gets typed wrong.
 *
 * Held as plain addresses rather than contact ids for the same reason: an
 * invitation goes to an inbox. Whether that inbox belongs to a CRM record is
 * something we note when it happens to be true, never something we require.
 *
 * The list is the same one Google is given, so removing somebody here and
 * saving does not just tidy this screen -- Google withdraws their invitation
 * and tells them so. That is said out loud wherever this is used.
 */
export function GuestPicker({
  companyId,
  value,
  onChange,
  disabled = false,
}: {
  /** Whose contacts to offer. Null simply means there are none to offer. */
  companyId: number | null;
  /** The addresses invited, lowercased. */
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const [typed, setTyped] = useState("");
  const [rejected, setRejected] = useState(false);

  const contacts = useQuery({
    queryKey: ["crm", "contacts", companyId],
    queryFn: () => crmApi.contacts(companyId!),
    enabled: companyId !== null,
    staleTime: 60_000,
  });

  const invited = new Set(value);

  const add = (email: string): void => {
    const clean = email.trim().toLowerCase();
    if (!clean) return;

    /* Refused rather than silently dropped. A pressed button that does nothing
       reads as a broken form, and the guest most worth catching is the one
       with a typo in it. */
    if (!looksLikeEmail(clean)) {
      setRejected(true);
      return;
    }

    setRejected(false);
    setTyped("");
    if (!invited.has(clean)) onChange([...value, clean]);
  };

  const remove = (email: string): void =>
    onChange(value.filter((entry) => entry !== email));

  /* Suppressed contacts are mailer-daemons and no-reply senders that the
     ingest heuristics filed under the account. Offering one as a guest is
     offering to invite a robot. */
  const offerable = (contacts.data ?? []).filter(
    (contact) =>
      !contact.isSuppressed && !invited.has(contact.email.toLowerCase()),
  );

  return (
    <div className="space-y-2">
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((email) => (
            <li key={email}>
              <span className="flex h-7 items-center gap-1.5 rounded-md border border-line bg-surface-2 pl-2 pr-1 text-xs text-content">
                {nameFor(email, contacts.data) ?? email}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => remove(email)}
                  aria-label={`Remove ${email}`}
                  className="rounded px-1 text-faint hover:text-brand-600 dark:hover:text-brand-400 disabled:opacity-50"
                >
                  ✕
                </button>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {offerable.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {offerable.map((contact) => (
            <Chip
              key={contact.id}
              on={false}
              onClick={() => {
                if (!disabled) add(contact.email);
              }}
            >
              + {contact.name ?? contact.email}
            </Chip>
          ))}
        </div>
      ) : null}

      <div className="flex gap-1.5">
        <input
          type="email"
          value={typed}
          disabled={disabled}
          onChange={(event) => {
            setTyped(event.target.value);
            setRejected(false);
          }}
          onKeyDown={(event) => {
            /*
             * Enter adds the address rather than submitting the form around
             * this. Typing somebody in and pressing Enter is the natural
             * gesture, and having it book the meeting instead -- with that
             * person left out of it -- is the exact failure this whole field
             * exists to fix.
             */
            if (event.key === "Enter" || event.key === ",") {
              event.preventDefault();
              add(typed);
            }
          }}
          onBlur={() => {
            /* A typed address left in the box is one somebody meant to invite.
               Losing it to a click elsewhere is silent data loss. */
            if (typed.trim()) add(typed);
          }}
          placeholder="Anyone else — type an email address"
          className={cn(
            "min-w-0 flex-1 rounded-md border bg-surface px-2.5 py-1.5 text-sm text-content placeholder:text-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            rejected ? "border-red-500" : "border-line",
          )}
        />
        <button
          type="button"
          disabled={disabled || !typed.trim()}
          onClick={() => add(typed)}
          className="shrink-0 rounded-md border border-line px-2.5 text-xs text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400 disabled:opacity-50"
        >
          Add
        </button>
      </div>

      {rejected ? (
        <p className="text-[11px] text-red-600">
          That does not look like an email address.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Deliberately loose.
 *
 * This is here to catch a fat-fingered address before it reaches Google, not
 * to adjudicate RFC 5322 -- which permits far stranger things than any regex
 * short of a parser will admit. Anything with a name, an @ and a dotted domain
 * gets through; the server validates again, and the real test is whether the
 * invitation arrives.
 */
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/** The contact's name when the address is one we know, else nothing. */
function nameFor(
  email: string,
  contacts: { email: string; name: string | null }[] | undefined,
): string | null {
  const match = contacts?.find(
    (contact) => contact.email.toLowerCase() === email,
  );
  return match?.name ?? null;
}
